import { useTranslations } from '@vocab/react';
import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useFormContext, type UseFormSetValue } from 'react-hook-form';

import { VirusScanStatus } from 'src/__generated__/graphql';
import { SELECTION_CRITERIA_KEY } from 'src/constants/app';
import { PageSection, useTrackingClient } from 'src/context/tracking';
import { uploadSelectionCriteria } from 'src/context/upload-attachments';
import { usePromises } from 'src/context/use-promises';
import {
  AttachmentOptionFile,
  AttachmentOptionText,
  AttachmentSelect,
  FileAttachment,
} from 'src/forms/common/AttachmentSelect/AttachmentSelect';
import {
  MAX_CHARS,
  useValidators,
} from 'src/forms/common/AttachmentSelect/hooks/useValidators';
import { useGetCurrentPage } from 'src/hooks/useGetCurrentPage';
import { useVirusScan } from 'src/hooks/useVirusScan';
import { isFile } from 'src/utils';

import translations from './.vocab';

export interface SelectionCriteriaFieldsValues {
  'selectionCriteria-method': FileAttachment;
  'selectionCriteria-uri': string;
  'selectionCriteria-file': File;
  'selectionCriteria-text': string;
  'selectionCriteria-virusScan': unknown;
}

interface Props {
  defaultMethod?: FileAttachment;
  defaultValue?: string | File;
  hideTitle?: boolean;
  SetValueOverride?: UseFormSetValue<SelectionCriteriaFieldsValues>;
  isLoadingOverride?: boolean;
  isVirusScanEnabled?: boolean;
}

export function SelectionCriteriaFields({
  defaultValue,
  defaultMethod,
  hideTitle = false,
  SetValueOverride,
  isLoadingOverride = false,
  isVirusScanEnabled = true,
}: Props) {
  const { t } = useTranslations(translations);
  const { trackDontIncludeSelectionCriteria } = useTracking();
  const { addPromise } = usePromises();
  const [isLoading, setIsLoading] = useState(isLoadingOverride);

  const { watch, setValue, trigger, setError, clearErrors, formState } =
    useFormContext<SelectionCriteriaFieldsValues>();

  const { validateMethod, validateFile, validateText } = useValidators({
    currentMethod: watch('selectionCriteria-method') || defaultMethod,
  });

  useVirusScan({
    fileUri: isVirusScanEnabled ? watch('selectionCriteria-uri') : undefined,
    currentMethod: watch('selectionCriteria-method'),
    onStatusChange: (virusScanStatus, method) => {
      if (!isVirusScanEnabled) {
        return;
      }

      const shouldCheck = method === FileAttachment.UPLOAD;

      if (!shouldCheck) {
        clearErrors('selectionCriteria-virusScan');
      } else if (virusScanStatus === VirusScanStatus.HasVirus) {
        setError('selectionCriteria-virusScan', {
          message: t('Virus Detected'),
        });
      } else {
        clearErrors('selectionCriteria-virusScan');
      }

      setIsLoading(virusScanStatus === VirusScanStatus.Pending);
    },
  });

  useEffect(() => {
    setIsLoading(isLoadingOverride);
  }, [isLoadingOverride]);

  const handleSelectionCriteriaChange = (selectionCriteria?: File) => {
    if (!selectionCriteria) {
      return;
    }
    setIsLoading(true);

    addPromise(
      SELECTION_CRITERIA_KEY,
      uploadSelectionCriteria({ selectionCriteria }),
      (res) => {
        if (res) {
          const setVal = SetValueOverride || setValue;
          flushSync(() => setVal('selectionCriteria-uri', res));
        }
        trigger('selectionCriteria-file');
      },
    );
  };

  return (
    <AttachmentSelect
      hideTitle={hideTitle}
      title={t('Selection criteria')}
      name="selectionCriteria-method"
      labelNone={t('Already addressed in resumé or cover letter')}
      defaultValue={defaultMethod}
      onChange={(method) => {
        if (method === FileAttachment.NONE) {
          trackDontIncludeSelectionCriteria();
        }
      }}
      validate={validateMethod}
    >
      <AttachmentOptionFile
        testid="selectionCriteriaFileInput"
        name="selectionCriteria-file"
        label={t('Upload a statement')}
        description={t('Accepted file types')}
        reUploadText={t('Upload a different statement')}
        validate={validateFile}
        onChange={handleSelectionCriteriaChange}
        defaultValue={isFile(defaultValue) ? defaultValue : undefined}
        isLoading={isLoading}
        message={formState.errors['selectionCriteria-virusScan']?.message}
      />
      <AttachmentOptionText
        testid="selectionCriteriaTextInput"
        name="selectionCriteria-text"
        label={t('Write a statement')}
        limit={MAX_CHARS}
        validate={validateText}
        defaultValue={
          typeof defaultValue === 'string' ? defaultValue : undefined
        }
      />
    </AttachmentSelect>
  );
}

export function useTracking() {
  const currentPage = useGetCurrentPage();
  const props = { currentPage };
  const client = useTrackingClient();

  function trackDontIncludeSelectionCriteria() {
    client.trackLink({
      ...props,
      link: 'selection_criteria_not_included_pressed',
      eventName: 'selection_criteria_not_included_pressed',
      currentPageSection: PageSection.SELECTION_CRITERIA,
    });
  }

  return { trackDontIncludeSelectionCriteria };
}
